import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSizeEnum,
  SupportRadioIcon,
  SupportTvIcon,
  Typography,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { BroadcasterScheduleProgramMiniCardCardActions } from '@/components/BroadcasterScheduleProgramMiniCardBase/components/BroadcasterScheduleProgramMiniCardCardActions';
import { RelaunchEnrichmentsModal } from '@/components/RelaunchEnrichmentsModal/RelaunchEnrichmentsModal';
import { StartTimeEndTimePart } from '@/components/StartTimeEndTimePart/StartTimeEndTimePart';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { TProgramBroadcastSegmentsPatch } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useChannelId } from '@/features/broadcasters/api/get-channel-id';
import { usePatchRelaunchSegment } from '@/features/segments/api/patch-relaunch-segment';
import { usePatchSegment } from '@/features/segments/api/patch-segment';
import { TReviewStateValues, TSegment, TEnrichmentsSelection } from '@/features/segments/types';
import { downloadFromUrl } from '@/helpers/download';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useReport } from '@/stores/report-store';

export function BroadcastScheduleProgramBodySegmentDetailCardHeader() {
  const { t } = useTranslation();
  const { activeSegment, setActiveSegment, setSegmentItems } = useBroadcastScheduleProgram();
  const { id = '' } = useParams();
  const { data: channelData } = useChannelId({ id: activeSegment?.channel_id || '' });
  const { mutateAsync: patchSegment } = usePatchSegment({ id });
  const { setReport, setReportedId, setReportType } = useReport();
  const relaunchDialog = useDisclosureWithData<TSegment>();
  const { mutate } = usePatchRelaunchSegment();

  const handleRelaunchSegment = async (id: string, enrichments: TEnrichmentsSelection) => {
    if (!id) return;
    mutate({ id, enrichments });
  };

  const handleStatusChange = async (status: string) => {
    if (status === '5') {
      setReportType('segment');
      setReport(true);
      setReportedId(activeSegment?.id || '');
    }
    const body: TProgramBroadcastSegmentsPatch = {
      ...activeSegment,
      id: activeSegment?.id || '',
      review_state: parseInt(status) as TReviewStateValues,
      uri: activeSegment?.uri || '',
      channel_id: activeSegment?.channel_id || '',
      media_type: activeSegment?.media_type || '',
      start_datetime: activeSegment?.start_datetime || '',
      end_datetime: activeSegment?.end_datetime || '',
      duration_in_seconds: activeSegment?.duration_in_seconds || 0,
      language_id: activeSegment?.language_id || '',
      programs: activeSegment?.programs.map(program => program.id) || [],
      segment_mentions: activeSegment?.segment_mentions || [],
      segment_transcriptions: activeSegment?.segment_transcriptions || [],
      segment_logos: activeSegment?.segment_logos || [],
      segment_peoples: activeSegment?.segment_peoples || [],
    };

    await patchSegment(body).then(data => {
      setActiveSegment(prev => {
        if (!prev) return prev;
        return { ...prev, review_state: data.review_state };
      });
      setSegmentItems(prev => {
        if (!prev) return prev;
        return prev.map(item => {
          if (item.id === data.id) {
            return { ...item, review_state: data.review_state };
          }
          return item;
        });
      });
    });
  };

  const uriName = useMemo(() => {
    const partes = activeSegment?.uri?.split('-') || '';
    const ultimaParte = partes[partes?.length - 1];
    return ultimaParte;
  }, [activeSegment?.uri]);

  if (!activeSegment) return <></>;

  return (
    <div className="flex gap-1">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {activeSegment?.media_type === 'tvts' ? <SupportTvIcon /> : <SupportRadioIcon />}
        <StatusSeenChipInTable
          item={activeSegment}
          review_state={activeSegment?.review_state || 0}
        />
        <Typography
          fontSize={FontSizeEnum.Sm}
          color={FontColorEnum.Medium}
          weight={FontWeightEnum.Semibold}
        >
          {channelData?.name}
        </Typography>
        <Typography
          fontSize={FontSizeEnum.Sm}
          color={FontColorEnum.Medium}
          weight={FontWeightEnum.Regular}
        >
          {uriName}
        </Typography>
        <StartTimeEndTimePart startDateTime={activeSegment?.start_datetime} />
      </div>
      <div className="flex items-center justify-end" data-id="segment-detail-header-actions">
        <StatusSeenChipSelect
          value={activeSegment?.review_state || 0}
          onChange={handleStatusChange}
          asIcon
          listOrientation="left"
        />
        <BroadcasterScheduleProgramMiniCardCardActions
          item={activeSegment}
          onAi={() => relaunchDialog.open(activeSegment)}
          size={IconButtonSizeEnum.SM}
          onDownload={downloadFromUrl}
        />
      </div>

      <RelaunchEnrichmentsModal
        isOpen={relaunchDialog.isOpen}
        onClose={relaunchDialog.close}
        onAction={enrichments => handleRelaunchSegment(relaunchDialog.data?.id || '', enrichments)}
        heading={t('Relanzar procesos de enriquecimiento')}
        actionLabel={t('Lanzar procesos')}
        allowAdvertising={false}
        isRadio={activeSegment?.media_type === 'rts'}
      />
    </div>
  );
}
